import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Locale } from './types';

const translations = {
  ru: {
    loading: 'Загрузка…',
    loadingSection: 'Загружаем данные',
    appSubtitle: 'Верификация входа и управление доступом к серверу',
    loginTitle: 'Введите код',
    loginDescription: 'Введите код, полученный от администрации.',
    loginButton: 'Войти',
    loginPlaceholder: 'Код верификации',
    loginLocked: 'Следующая попытка доступна через {seconds} сек.',
    logout: 'Выйти',
    language: 'Язык',
    russian: 'Русский',
    english: 'English',
    server: 'Сервер',
    allServers: 'Все серверы',
    overview: 'Профиль',
    pendingAuth: 'Подтверждение входа',
    notifications: 'Уведомления',
    servers: 'Серверы',
    invites: 'Коды',
    codeCreation: 'Создание кодов',
    codeChecking: 'Проверка кодов',
    codeList: 'Список кодов',
    players: 'Игроки',
    adminPending: 'Ожидающие IP',
    serverStats: 'Статистика',
    commandQueue: 'Консоль',
    audit: 'Логи',
    errors: 'Ошибки',
    broadcast: 'Рассылка',
    profile: 'Профиль',
    skin: 'Скин',
    bindNickname: 'Привязка никнейма',
    changeNickname: 'Сменить ник',
    passwordChangeSection: 'Смена пароля',
    nickname: 'Никнейм',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    confirmNewPassword: 'Подтверждение нового пароля',
    changePassword: 'Сменить пароль',
    save: 'Сохранить',
    cancel: 'Отмена',
    now: 'Сейчас',
    never: 'Никогда',
    create: 'Создать',
    generatingCodes: 'Идёт генерация...',
    refresh: 'Обновить',
    search: 'Поиск',
    filter: 'Фильтр',
    state: 'Состояние',
    status: 'Статус',
    actions: 'Действия',
    createdAt: 'Создано',
    updatedAt: 'Обновлено',
    activatedAt: 'Активирован',
    revokedAt: 'Отозван',
    expiresAt: 'Истекает',
    lastLoginAt: 'Последний вход',
    lastSeenIp: 'Текущий IP',
    inviter: 'Пригласил',
    creator: 'Создал',
    notSet: 'Не указано',
    yes: 'Да',
    no: 'Нет',
    approve: 'Да',
    reject: 'Нет',
    thisIsYou: 'Это вы пытаетесь зайти на сервер?',
    emptyState: 'Пока пусто',
    active: 'Активен',
    inactive: 'Неактивен',
    disableNicknameChange: 'Отключить смену ника',
    nicknameChangeDisabled: 'Смена ника отключена',
    revoked: 'Отозван',
    activated: 'Активирован',
    all: 'Все',
    title: 'Заголовок',
    body: 'Текст',
    severity: 'Важность',
    info: 'Инфо',
    success: 'Успешно',
    warning: 'Предупреждение',
    error: 'Ошибка',
    errorId: 'ID ошибки',
    diagnosticCode: 'Код диагностики',
    endpoint: 'Эндпоинт',
    scope: 'Область',
    global: 'Глобально',
    serverScope: 'По серверу',
    description: 'Описание',
    name: 'Название',
    slug: 'Тех. название',
    permissions: 'Права',
    count: 'Количество',
    labelPrefix: 'Описание кода',
    plainCodes: 'Новые коды (показываются один раз)',
    kickIfOnline: 'Кикнуть онлайн-игрока',
    reason: 'Причина',
    inviteCodeId: 'ID кода',
    profileId: 'ID профиля',
    ipAddress: 'IP-адрес',
    authorizedIps: 'Авторизованные IP',
    source: 'Источник',
    lastUsedAt: 'Последнее использование',
    note: 'Заметка',
    command: 'Команда',
    executeAfter: 'Выполнить после',
    actor: 'Инициатор',
    eventType: 'Событие',
    entity: 'Сущность',
    metadata: 'Метаданные',
    openAdminTools: 'Инструменты администратора',
    noAccess: 'Недостаточно прав для этого раздела.',
    statsTotalInvites: 'Всего кодов',
    statsActivatedInvites: 'Активировано',
    statsInactiveInvites: 'Не активировано',
    statsRevokedInvites: 'Отозвано',
    statsBoundNicknames: 'Привязанных ников',
    statsActiveAuthorizedIps: 'Авторизованных IP',
    statsPendingIpRequests: 'Ожидающие IP',
    statsActiveNotifications: 'Активных уведомлений',
    profileSummary: 'Краткая сводка',
    summaryPendingAuth: 'Ожидают подтверждения',
    summaryUnreadNotifications: 'Непрочитанных уведомлений',
    selectPlayerHint: 'Выберите игрока из списка или укажите идентификатор вручную.',
    resetAuthorizedIps: 'Сбросить авторизацию IP',
    resetLoginPassword: 'Сбросить пароль входа',
    forceBindIp: 'Привязать IP',
    forceChangeNickname: 'Сменить ник принудительно',
    banByCode: 'Забанить код',
    delete: 'Удалить',
    deleteAuthorizedIpConfirm: 'Удалить этот IP из списка авторизованных?',
    authorizedIpDeleted: 'IP удалён из списка авторизованных.',
    deleteNotificationConfirm: 'Удалить это уведомление? Оно пропадёт у всех получателей.',
    notificationDeleted: 'Уведомление удалено.',
    edit: 'Редактировать',
    update: 'Обновить',
    pagination: 'Навигация по страницам',
    previousPage: 'Назад',
    nextPage: 'Вперёд',
    pageNumber: '{page}',
    createServer: 'Создать сервер',
    createInviteCodes: 'Создать коды',
    queueCommand: 'Поставить команду в очередь',
    sendNotification: 'Отправить уведомление',
    editNotification: 'Редактировать уведомление',
    createdNotifications: 'Созданные уведомления',
    requestStatusPending: 'Ожидает',
    requestStatusApproved: 'Подтверждено',
    requestStatusRejected: 'Отклонено',
    requestStatusExpired: 'Просрочено',
    requestStatusCancelled: 'Отменено',
    queueStatusPending: 'Ожидает',
    queueStatusProcessing: 'В работе',
    queueStatusDone: 'Выполнено',
    queueStatusFailed: 'Ошибка',
    queueStatusCancelled: 'Отменено',
    dashboardGreeting: 'Здравствуйте, {name}',
    dashboardDescription: 'Этот кабинет показывает только те действия, на которые у текущего кода есть права.',
    sectionDescOverview: 'Профиль игрока, текущий IP, авторизованные IP и настройки аккаунта.',
    sectionDescSkin: 'Смена скина по нику, прямой texture URL или data/signature.',
    sectionDescPendingAuth: 'Подтверждение или отклонение входов с новых IP.',
    sectionDescNotifications: 'Лента уведомлений от системы и администрации.',
    sectionDescStatistics: 'Серверные показатели по кодам, никам, авторизованным IP и уведомлениям.',
    sectionDescServers: 'Создание и редактирование серверов и их настроек.',
    sectionDescInviteCreate: 'Генерация новых кодов доступа и назначение прав.',
    sectionDescInviteCheck: 'Проверка исходных кодов доступа или SHA-256 хэшей.',
    sectionDescInviteList: 'Список созданных кодов и управление их правами.',
    sectionDescPlayers: 'Список игроков и админ-действия по нику, IP и банам.',
    sectionDescAdminPending: 'Общий список ожидающих IP-запросов по выбранному серверу.',
    sectionDescBroadcast: 'Отправка серверных и глобальных уведомлений.',
    sectionDescCommandQueue: 'Очередь команд и контроль статусов их выполнения.',
    sectionDescAudit: 'Логи действий и изменений в системе.',
    sectionDescErrors: 'Ошибки API с диагностическими кодами для разбора.',
    adminDescription: 'Все операции сайта и плагинов синхронизируются только через PostgreSQL.',
    notificationRead: 'Прочитано',
    markRead: 'Прочитано',
    markAllRead: 'Прочитано всё',
    notificationsMarkedRead: 'Уведомления отмечены прочитанными.',
    refreshSection: 'Обновить раздел',
    formSaved: 'Изменения сохранены.',
    passwordChanged: 'Пароль входа обновлён.',
    passwordConfirmationMismatch: 'Подтверждение нового пароля не совпадает.',
    formCreated: 'Запись создана.',
    targetNickname: 'Целевой ник',
    targetInvite: 'Целевой код',
    targetProfile: 'Целевой профиль',
    createdCodesHelp: 'Сохраните эти коды сразу: в базе хранится только их SHA-256 хэш.',
    loginPrompt: 'Подтвердите вход через Eternal Verifier',
    permission_Bind_Nickname: 'Привязка никнейма',
    permission_Authorize_Login: 'Подтверждение входа',
    permission_View_Profile: 'Просмотр профиля',
    permission_Send_Global_Notification: 'Отправка уведомлений',
    permission_Create_Invites: 'Создание кодов',
    permission_View_Server_Stats: 'Статистика сервера',
    permission_Force_Bind_Ip: 'Принудительная привязка IP',
    permission_View_Player_Or_Code_Info: 'Информация по игрокам/кодам',
    permission_Ban_By_Code: 'Бан по коду',
    permission_Force_Change_Nickname: 'Принудительная смена ника',
    permission_Server_Console: 'Консоль сервера'
  },
  en: {
    loading: 'Loading…',
    loadingSection: 'Loading data',
    appSubtitle: 'Login verification and Minecraft server access management',
    loginTitle: 'Enter your code',
    loginDescription: 'Enter the code provided by the administration.',
    loginButton: 'Sign in',
    loginPlaceholder: 'Verification code',
    loginLocked: 'Next attempt available in {seconds} sec.',
    logout: 'Log out',
    language: 'Language',
    russian: 'Русский',
    english: 'English',
    server: 'Server',
    allServers: 'All servers',
    overview: 'Profile',
    pendingAuth: 'Login approval',
    notifications: 'Notifications',
    servers: 'Servers',
    invites: 'Invite codes',
    codeCreation: 'Create codes',
    codeChecking: 'Check codes',
    codeList: 'Code list',
    players: 'Players',
    adminPending: 'Pending IP',
    serverStats: 'Statistics',
    commandQueue: 'Console',
    audit: 'Logs',
    errors: 'Errors',
    broadcast: 'Broadcast',
    profile: 'Profile',
    skin: 'Skin',
    bindNickname: 'Bind nickname',
    changeNickname: 'Change nickname',
    passwordChangeSection: 'Password change',
    nickname: 'Nickname',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    changePassword: 'Change password',
    save: 'Save',
    cancel: 'Cancel',
    now: 'Now',
    never: 'Never',
    create: 'Create',
    generatingCodes: 'Generating...',
    refresh: 'Refresh',
    search: 'Search',
    filter: 'Filter',
    state: 'State',
    status: 'Status',
    actions: 'Actions',
    createdAt: 'Created at',
    updatedAt: 'Updated at',
    activatedAt: 'Activated at',
    revokedAt: 'Revoked at',
    expiresAt: 'Expires at',
    lastLoginAt: 'Last login',
    lastSeenIp: 'Current IP',
    inviter: 'Invited by',
    creator: 'Created by',
    notSet: 'Not set',
    yes: 'Yes',
    no: 'No',
    approve: 'Yes',
    reject: 'No',
    thisIsYou: 'Is this you trying to join the server?',
    emptyState: 'Nothing here yet',
    active: 'Active',
    inactive: 'Inactive',
    disableNicknameChange: 'Disable nickname change',
    nicknameChangeDisabled: 'Nickname change disabled',
    revoked: 'Revoked',
    activated: 'Activated',
    all: 'All',
    title: 'Title',
    body: 'Body',
    severity: 'Severity',
    info: 'Info',
    success: 'Success',
    warning: 'Warning',
    error: 'Error',
    errorId: 'Error ID',
    diagnosticCode: 'Diagnostic code',
    endpoint: 'Endpoint',
    scope: 'Scope',
    global: 'Global',
    serverScope: 'Server',
    description: 'Description',
    name: 'Name',
    slug: 'Slug',
    permissions: 'Permissions',
    count: 'Count',
    labelPrefix: 'Label prefix',
    plainCodes: 'New codes (shown only once)',
    kickIfOnline: 'Kick if online',
    reason: 'Reason',
    inviteCodeId: 'Invite code ID',
    profileId: 'Profile ID',
    ipAddress: 'IP address',
    authorizedIps: 'Authorized IPs',
    source: 'Source',
    lastUsedAt: 'Last used',
    note: 'Note',
    command: 'Command',
    executeAfter: 'Execute after',
    actor: 'Actor',
    eventType: 'Event type',
    entity: 'Entity',
    metadata: 'Metadata',
    openAdminTools: 'Administration tools',
    noAccess: 'You do not have permission for this section.',
    statsTotalInvites: 'Total invites',
    statsActivatedInvites: 'Activated',
    statsInactiveInvites: 'Inactive',
    statsRevokedInvites: 'Revoked',
    statsBoundNicknames: 'Bound nicknames',
    statsActiveAuthorizedIps: 'Authorized IPs',
    statsPendingIpRequests: 'Pending IP',
    statsActiveNotifications: 'Active notifications',
    profileSummary: 'Quick summary',
    summaryPendingAuth: 'Pending approvals',
    summaryUnreadNotifications: 'Unread notifications',
    selectPlayerHint: 'Pick a player from the table or provide the identifiers manually.',
    resetAuthorizedIps: 'Reset IP authorization',
    resetLoginPassword: 'Reset login password',
    forceBindIp: 'Bind IP',
    forceChangeNickname: 'Force nickname change',
    banByCode: 'Ban by code',
    delete: 'Delete',
    deleteAuthorizedIpConfirm: 'Remove this IP from the authorized list?',
    authorizedIpDeleted: 'IP removed from the authorized list.',
    deleteNotificationConfirm: 'Delete this notification? It will disappear for all recipients.',
    notificationDeleted: 'Notification deleted.',
    edit: 'Edit',
    update: 'Update',
    pagination: 'Page navigation',
    previousPage: 'Previous',
    nextPage: 'Next',
    pageNumber: '{page}',
    createServer: 'Create server',
    createInviteCodes: 'Create invite codes',
    queueCommand: 'Queue command',
    sendNotification: 'Send notification',
    editNotification: 'Edit notification',
    createdNotifications: 'Created notifications',
    requestStatusPending: 'Pending',
    requestStatusApproved: 'Approved',
    requestStatusRejected: 'Rejected',
    requestStatusExpired: 'Expired',
    requestStatusCancelled: 'Cancelled',
    queueStatusPending: 'Pending',
    queueStatusProcessing: 'Processing',
    queueStatusDone: 'Done',
    queueStatusFailed: 'Failed',
    queueStatusCancelled: 'Cancelled',
    dashboardGreeting: 'Hello, {name}',
    dashboardDescription: 'This dashboard only shows the actions available to the current invite code.',
    sectionDescOverview: 'Player profile, current IP, authorized IPs, and account settings.',
    sectionDescSkin: 'Change skin by nickname, direct texture URL, or data/signature.',
    sectionDescPendingAuth: 'Approve or reject login attempts from new IP addresses.',
    sectionDescNotifications: 'Notification feed from the system and administrators.',
    sectionDescStatistics: 'Server metrics for invite codes, nicknames, authorized IPs, and notifications.',
    sectionDescServers: 'Create and edit servers and their settings.',
    sectionDescInviteCreate: 'Generate new invite codes and assign permissions.',
    sectionDescInviteCheck: 'Check plain invite codes or SHA-256 hashes.',
    sectionDescInviteList: 'List created codes and manage their permissions.',
    sectionDescPlayers: 'Player list with admin actions for nickname, IP, and bans.',
    sectionDescAdminPending: 'Global list of pending IP requests for the selected server.',
    sectionDescBroadcast: 'Send server-scoped or global notifications.',
    sectionDescCommandQueue: 'Queue commands and track their execution status.',
    sectionDescAudit: 'Action and change logs.',
    sectionDescErrors: 'API errors with diagnostic codes for troubleshooting.',
    adminDescription: 'All website and plugin synchronization happens only through PostgreSQL.',
    notificationRead: 'Read',
    markRead: 'Mark as read',
    markAllRead: 'Mark all read',
    notificationsMarkedRead: 'Notifications marked as read.',
    refreshSection: 'Refresh section',
    formSaved: 'Changes saved.',
    passwordChanged: 'Login password updated.',
    passwordConfirmationMismatch: 'New password confirmation does not match.',
    formCreated: 'Entry created.',
    targetNickname: 'Target nickname',
    targetInvite: 'Target invite',
    targetProfile: 'Target profile',
    createdCodesHelp: 'Store these codes immediately: only their SHA-256 hashes remain in the database.',
    loginPrompt: 'Confirm login through Eternal Verifier',
    permission_Bind_Nickname: 'Bind nickname',
    permission_Authorize_Login: 'Approve login',
    permission_View_Profile: 'View profile',
    permission_Send_Global_Notification: 'Send notifications',
    permission_Create_Invites: 'Create invite codes',
    permission_View_Server_Stats: 'View server stats',
    permission_Force_Bind_Ip: 'Force IP binding',
    permission_View_Player_Or_Code_Info: 'View players/code info',
    permission_Ban_By_Code: 'Ban by code',
    permission_Force_Change_Nickname: 'Force nickname change',
    permission_Server_Console: 'Server console'
  }
} as const;

type TranslationKey = keyof typeof translations.ru;

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function interpolate(template: string, vars: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? `{${key}}`));
}

export function I18nProvider({ children, defaultLocale }: { children: ReactNode; defaultLocale: Locale }) {
  const [locale, setLocale] = useState<Locale>(() => {
    const stored = window.localStorage.getItem('ev_locale');
    return stored === 'en' || stored === 'ru' ? stored : defaultLocale;
  });

  useEffect(() => {
    window.localStorage.setItem('ev_locale', locale);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    return {
      locale,
      setLocale,
      t: (key, vars) => interpolate(translations[locale][key], vars)
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
}

export function permissionLabel(locale: Locale, permission: string): string {
  if (permission === 'PRIORITY_SPEED') {
    return locale === 'ru' ? 'Приоритетная скорость' : 'Priority speed';
  }

  const key = `permission_${permission
    .toLowerCase()
    .split('_')
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join('_')}` as TranslationKey;

  return translations[locale][key] ?? permission;
}
